import React, { useState, useEffect } from "react";
import CustomerLayout from "./CustomerLayout";
import { API_URL } from "../config";
import {
    Globe,
    Calendar,
    CheckCircle2,
    DollarSign,
    Search,
    Building2,
    Ticket,
    Eye,
    Pencil,
    Download,
    Trash2,
    Users,
    Clock,
    Compass,
    X,
    Loader2,
    AlertTriangle,
    Ban,
    RefreshCw,
    MapPin,
    FileText,
} from "lucide-react";

interface ActivityBooking {
    id: number;
    activity_id: number;
    user_id: number | null;
    client_id: number | null;
    firstname: string;
    lastname: string;
    email: string;
    whatsapp_number: string;
    preferred_date: string;
    guests: number;
    status: "pending" | "confirmed" | "cancelled";
    notes: string | null;
    created_at: string;
    updated_at: string;
    activity: {
        id: number;
        name: string;
        description: string;
        price: string | number;
        duration: string;
        location: string;
        image: string | null;
    } | null;
}

interface FormattedActivity {
    id: number;
    destination: string;
    hotel: string;
    tourPackage: string;
    bookingDate: string;
    startDate: string;
    endDate: string;
    amount: number;
    status: "Confirmed" | "Pending" | "Completed" | "Cancelled";
    travelers: number;
    paymentStatus: "Paid" | "Partial" | "Pending";
    bookingReference: string;
    fullData: ActivityBooking;
}

const MyTrips: React.FC = () => {
    const [bookings, setBookings] = useState<FormattedActivity[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState<string>("All Trips");

    // Modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [bookingToDelete, setBookingToDelete] = useState<FormattedActivity | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [bookingToCancel, setBookingToCancel] = useState<FormattedActivity | null>(null);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [cancelError, setCancelError] = useState<string | null>(null);

    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
    const [selectedBooking, setSelectedBooking] = useState<FormattedActivity | null>(null);
    const [detailsLoading, setDetailsLoading] = useState<boolean>(false);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const mapStatus = (status: string): FormattedActivity["status"] => {
        const map: Record<string, FormattedActivity["status"]> = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'completed': 'Completed'
        };
        return map[status] || 'Pending';
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'confirmed': return { bg: '#e6f4ea', text: '#1e7e34', dot: '#1e7e34' };
            case 'pending': return { bg: '#fef3d6', text: '#b25900', dot: '#b25900' };
            case 'completed': return { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' };
            case 'cancelled': return { bg: '#fce8e6', text: '#c5221f', dot: '#c5221f' };
            default: return { bg: '#f0f0f0', text: '#666', dot: '#666' };
        }
    };

    const canCancel = (status: string): boolean => {
        return ['pending'].includes(status);
    };

    const canDelete = (status: string): boolean => {
        return ['pending', 'cancelled'].includes(status);
    };

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(`${API_URL}/customer/my-activity-bookings`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch your activity bookings.");
                }

                const payload = await response.json();
                const rawData = payload.data || [];

                const formatted: FormattedActivity[] = rawData.map((item: ActivityBooking) => ({
                    id: item.id,
                    destination: item.activity?.location || item.activity?.name || "Activity",
                    hotel: item.activity?.name || "Activity Booking",
                    tourPackage: item.activity?.description?.substring(0, 50) || "Activity Package",
                    bookingDate: formatDate(item.created_at),
                    startDate: formatDate(item.preferred_date),
                    endDate: formatDate(item.preferred_date),
                    amount: Number(item.activity?.price) || 0,
                    status: mapStatus(item.status),
                    travelers: item.guests || 1,
                    paymentStatus: item.status === 'confirmed' ? 'Paid' : 'Pending',
                    bookingReference: `ACT-${String(item.id).padStart(6, '0')}`,
                    fullData: item,
                }));

                setBookings(formatted);
            } catch (err: any) {
                console.error("Fetch Activity Bookings Error:", err);
                setError(err.message || "Unable to load activity bookings.");
                setBookings([]);
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, []);

    // Delete handlers
    const openDeleteModal = (booking: FormattedActivity) => {
        setBookingToDelete(booking);
        setDeleteError(null);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!bookingToDelete) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-activity-bookings/${bookingToDelete.id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete booking.");
            }

            setBookings((prev) => prev.filter((b) => b.id !== bookingToDelete.id));
            setIsDeleteModalOpen(false);
            setBookingToDelete(null);
        } catch (err: any) {
            setDeleteError(err.message || "Deletion error.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Cancel handlers
    const openCancelModal = (booking: FormattedActivity) => {
        setBookingToCancel(booking);
        setCancelError(null);
        setIsCancelModalOpen(true);
    };

    const confirmCancel = async () => {
        if (!bookingToCancel) return;

        setIsCancelling(true);
        setCancelError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-activity-bookings/${bookingToCancel.id}/cancel`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to cancel booking.");
            }

            setBookings((prev) =>
                prev.map((b) => (b.id === bookingToCancel.id ? { ...b, status: "Cancelled" as FormattedActivity["status"] } : b))
            );
            setIsCancelModalOpen(false);
            setBookingToCancel(null);
        } catch (err: any) {
            setCancelError(err.message || "Cancellation error.");
        } finally {
            setIsCancelling(false);
        }
    };

    // View details
    const handleViewDetails = async (booking: FormattedActivity) => {
        setSelectedBooking(booking);
        setIsDetailsModalOpen(true);
        setDetailsLoading(true);
        
        try {
            const response = await fetch(`${API_URL}/customer/my-activity-bookings/${booking.id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (response.ok) {
                const payload = await response.json();
                const updatedData = payload.data;
                if (updatedData) {
                    setSelectedBooking({
                        ...booking,
                        fullData: updatedData,
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching booking details:", err);
        } finally {
            setDetailsLoading(false);
        }
    };

    // Stats
    const totalBookings = bookings.length;
    const upcomingBookings = bookings.filter(
        (b) => b.status === "Confirmed" && new Date(b.startDate) >= new Date()
    ).length;
    const completedBookings = bookings.filter((b) => b.status === "Completed").length;
    const totalSpent = bookings
        .filter((b) => b.status !== "Cancelled")
        .reduce((sum, b) => sum + b.amount, 0);

    const filteredBookings = bookings.filter((booking) => {
        const matchesSearch =
            booking.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.hotel.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.tourPackage.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.bookingReference.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (activeFilter === "Upcoming") {
            return booking.status === "Confirmed" && new Date(booking.startDate) >= new Date();
        }
        if (activeFilter === "Completed") return booking.status === "Completed";
        if (activeFilter === "Cancelled") return booking.status === "Cancelled";

        return true;
    });

    return (
        <CustomerLayout>
            <style>{`
                .trips-page-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 24px 40px;
                }

                .page-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .page-header-modern h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 4px 0;
                }

                .page-header-modern p {
                    font-size: 15px;
                    color: #6b7280;
                    margin: 0;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .btn-refresh {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-refresh:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .trips-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }

                .trip-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .trip-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }

                .trip-stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .trip-stat-content h4 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                    line-height: 1.2;
                }

                .trip-stat-content p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }

                .filters-modern {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 16px 20px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }

                .search-modern {
                    position: relative;
                    flex: 1;
                    min-width: 240px;
                }

                .search-modern input {
                    width: 100%;
                    padding: 10px 14px 10px 40px;
                    border-radius: 10px;
                    border: 1px solid #e5e7eb;
                    background: #f9fafb;
                    font-size: 14px;
                    outline: none;
                    color: #1a1a2e;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }

                .search-modern input:focus {
                    border-color: #294b5a;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(41, 75, 90, 0.08);
                }

                .search-modern svg {
                    position: absolute;
                    left: 13px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                }

                .filter-group {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .filter-btn-modern {
                    padding: 8px 18px;
                    border: 1px solid #e5e7eb;
                    border-radius: 20px;
                    background: #ffffff;
                    font-size: 13px;
                    font-weight: 500;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .filter-btn-modern:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .filter-btn-modern.active {
                    background: #294b5a;
                    color: #ffffff;
                    border-color: #294b5a;
                }

                .trips-container {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }

                .trip-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 24px;
                    border: 1px solid #f0f0f0;
                    transition: all 0.3s ease;
                }

                .trip-card:hover {
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border-color: #e5e7eb;
                }

                .trip-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 16px;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .trip-destination {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .destination-icon {
                    width: 44px;
                    height: 44px;
                    background: #f3f4f6;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #294b5a;
                    flex-shrink: 0;
                }

                .destination-info h3 {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 4px 0;
                }

                .booking-ref {
                    font-size: 12px;
                    color: #6b7280;
                    font-family: monospace;
                    font-weight: 600;
                }

                .trip-badges {
                    display: flex;
                    gap: 8px;
                }

                .badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .badge-confirmed { background: #e6f4ea; color: #1e7e34; }
                .badge-pending { background: #fef3d6; color: #b25900; }
                .badge-completed { background: #e8f0fe; color: #1a73e8; }
                .badge-cancelled { background: #fce8e6; color: #c5221f; }

                .badge-paid { background: #e6f4ea; color: #1e7e34; }
                .badge-partial { background: #fef3d6; color: #b25900; }

                .trip-details-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 16px;
                    padding: 16px 0;
                    border-top: 1px solid #edf2f4;
                    border-bottom: 1px solid #edf2f4;
                }

                .detail-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .detail-icon {
                    width: 36px;
                    height: 36px;
                    background: #f7f5f1;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #7a8a91;
                }

                .detail-content {
                    display: flex;
                    flex-direction: column;
                }

                .detail-label {
                    font-size: 11px;
                    color: #7a8a91;
                    text-transform: uppercase;
                    font-weight: 600;
                }

                .detail-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .trip-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .trip-meta {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    color: #6b7280;
                }

                .action-buttons-group {
                    display: flex;
                    gap: 8px;
                }

                .action-btn-modern {
                    background: transparent;
                    border: 1px solid #e5e7eb;
                    cursor: pointer;
                    padding: 8px 14px;
                    border-radius: 8px;
                    transition: all 0.2s;
                    color: #374151;
                    font-size: 13px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                .action-btn-modern:hover {
                    background: #f9fafb;
                }

                .action-btn-modern.primary {
                    background: #294b5a;
                    color: #ffffff;
                    border-color: #294b5a;
                }

                .action-btn-modern.primary:hover {
                    background: #1a313d;
                }

                .action-btn-modern.delete:hover {
                    background: #fef2f2;
                    color: #dc2626;
                    border-color: #fef2f2;
                }

                .empty-state-modern {
                    text-align: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                }

                .empty-state-modern svg {
                    color: #d1d5db;
                    margin-bottom: 16px;
                }

                .empty-state-modern h3 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .empty-state-modern p {
                    color: #6b7280;
                    font-size: 14px;
                    margin: 0;
                }

                .loading-state-modern {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    gap: 16px;
                }

                .spinner-modern {
                    animation: spin 0.8s linear infinite;
                    color: #294b5a;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .error-box-modern {
                    padding: 14px 18px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                .modal-content {
                    background: #ffffff;
                    border-radius: 20px;
                    max-width: 650px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                    padding: 32px;
                    position: relative;
                    animation: slideUp 0.25s ease;
                }

                .modal-content.delete-modal {
                    max-width: 440px;
                    text-align: center;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #edf2f4;
                    margin-bottom: 4px;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #1a1a2e;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                }

                .modal-close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close-btn:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .modal-body {
                    margin-top: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .modal-section {
                    background: #f8fafc;
                    padding: 18px 20px;
                    border-radius: 12px;
                    border: 1px solid #eef2f6;
                }

                .modal-section-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    margin-bottom: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .details-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                }

                .detail-item-modal {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .detail-label-modal {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-value-modal {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .delete-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #fef2f2;
                    color: #dc2626;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px auto;
                }

                .delete-modal-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .delete-modal-desc {
                    font-size: 14px;
                    color: #6b7280;
                    margin: 0 0 20px 0;
                    line-height: 1.6;
                }

                .delete-summary {
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #eef2f6;
                }

                .delete-actions {
                    display: flex;
                    gap: 12px;
                }

                .btn-cancel-modal {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    background: #ffffff;
                    color: #374151;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-cancel-modal:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .btn-cancel-modal:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .btn-delete-confirm {
                    flex: 1;
                    padding: 12px 16px;
                    border: none;
                    background: #dc2626;
                    color: #ffffff;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .btn-delete-confirm:hover {
                    background: #b91c1c;
                }

                .btn-delete-confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .badge-status-modal {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .badge-confirmed { background: #e6f4ea; color: #1e7e34; }
                .badge-pending { background: #fef3d6; color: #b25900; }
                .badge-cancelled { background: #fce8e6; color: #c5221f; }
                .badge-completed { background: #e8f0fe; color: #1a73e8; }

                .error-box {
                    padding: 12px 16px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                }

                @media (max-width: 1024px) {
                    .trip-card {
                        padding: 20px;
                    }

                    .trip-details-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .trips-page-container {
                        padding: 0 16px 30px;
                    }

                    .page-header-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .header-actions {
                        flex-wrap: wrap;
                    }

                    .trips-stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .filters-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .search-modern {
                        min-width: unset;
                    }

                    .filter-group {
                        justify-content: center;
                    }

                    .trip-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .trip-badges {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .trip-footer {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .trip-meta {
                        flex-wrap: wrap;
                    }

                    .action-buttons-group {
                        flex-wrap: wrap;
                    }

                    .trip-details-grid {
                        grid-template-columns: 1fr;
                    }

                    .modal-content {
                        padding: 24px 20px;
                        margin: 10px;
                        max-height: 95vh;
                    }

                    .details-grid {
                        grid-template-columns: 1fr;
                        gap: 10px;
                    }

                    .delete-actions {
                        flex-direction: column;
                    }

                    .modal-header h3 {
                        font-size: 18px;
                    }
                }

                @media (max-width: 480px) {
                    .trips-stats-grid {
                        grid-template-columns: 1fr 1fr;
                        gap: 10px;
                    }

                    .trip-stat-card {
                        padding: 14px 16px;
                    }

                    .trip-stat-content h4 {
                        font-size: 20px;
                    }

                    .trip-card {
                        padding: 16px;
                    }

                    .action-btn-modern {
                        padding: 6px 10px;
                        font-size: 12px;
                    }
                }
            `}</style>

            <div className="trips-page-container">
                {/* Header */}
                <div className="page-header-modern">
                    <div>
                        <h1>My Activity Bookings</h1>
                        <p>View, filter and manage all your activity reservations</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={() => window.location.reload()}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && <div className="error-box-modern">{error}</div>}

                {/* Stats */}
                <div className="trips-stats-grid">
                    <div className="trip-stat-card">
                        <div className="trip-stat-icon" style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                            <Globe size={24} />
                        </div>
                        <div className="trip-stat-content">
                            <h4>{totalBookings}</h4>
                            <p>Total Bookings</p>
                        </div>
                    </div>

                    <div className="trip-stat-card">
                        <div className="trip-stat-icon" style={{ background: '#fef3d6', color: '#b25900' }}>
                            <Clock size={24} />
                        </div>
                        <div className="trip-stat-content">
                            <h4>{upcomingBookings}</h4>
                            <p>Upcoming</p>
                        </div>
                    </div>

                    <div className="trip-stat-card">
                        <div className="trip-stat-icon" style={{ background: '#e6f4ea', color: '#1e7e34' }}>
                            <CheckCircle2 size={24} />
                        </div>
                        <div className="trip-stat-content">
                            <h4>{completedBookings}</h4>
                            <p>Completed</p>
                        </div>
                    </div>

                    <div className="trip-stat-card">
                        <div className="trip-stat-icon" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="trip-stat-content">
                            <h4>OMR {totalSpent.toLocaleString()}</h4>
                            <p>Total Spent</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-modern">
                    <div className="search-modern">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search by activity, location or reference..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        {["All Trips", "Upcoming", "Completed", "Cancelled"].map((filter) => (
                            <button
                                key={filter}
                                className={`filter-btn-modern ${activeFilter === filter ? "active" : ""}`}
                                onClick={() => setActiveFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Bookings List */}
                {loading ? (
                    <div className="loading-state-modern">
                        <Loader2 className="spinner-modern" size={36} />
                        <span style={{ color: '#6b7280' }}>Loading your activity bookings...</span>
                    </div>
                ) : filteredBookings.length > 0 ? (
                    <div className="trips-container">
                        {filteredBookings.map((booking) => {
                            const statusColor = getStatusColor(booking.status);
                            return (
                                <div key={booking.id} className="trip-card">
                                    <div className="trip-header">
                                        <div className="trip-destination">
                                            <div className="destination-icon">
                                                <Compass size={22} />
                                            </div>
                                            <div className="destination-info">
                                                <h3>{booking.destination}</h3>
                                                <span className="booking-ref">Ref: {booking.bookingReference}</span>
                                            </div>
                                        </div>
                                        <div className="trip-badges">
                                            <span 
                                                className="badge"
                                                style={{ background: statusColor.bg, color: statusColor.text }}
                                            >
                                                {booking.status}
                                            </span>
                                            <span className={`badge ${booking.paymentStatus === 'Paid' ? 'badge-paid' : 'badge-partial'}`}>
                                                {booking.paymentStatus}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="trip-details-grid">
                                        <div className="detail-item">
                                            <div className="detail-icon">
                                                <Building2 size={18} />
                                            </div>
                                            <div className="detail-content">
                                                <span className="detail-label">Activity</span>
                                                <span className="detail-value">{booking.hotel}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <div className="detail-icon">
                                                <Ticket size={18} />
                                            </div>
                                            <div className="detail-content">
                                                <span className="detail-label">Package</span>
                                                <span className="detail-value">{booking.tourPackage}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <div className="detail-icon">
                                                <Calendar size={18} />
                                            </div>
                                            <div className="detail-content">
                                                <span className="detail-label">Date</span>
                                                <span className="detail-value">{booking.startDate}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <div className="detail-icon">
                                                <DollarSign size={18} />
                                            </div>
                                            <div className="detail-content">
                                                <span className="detail-label">Amount</span>
                                                <span className="detail-value">OMR {booking.amount.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="trip-footer">
                                        <div className="trip-meta">
                                            <div className="meta-item">
                                                <Calendar size={14} />
                                                <span>Booked: {booking.bookingDate}</span>
                                            </div>
                                            <div className="meta-item">
                                                <Users size={14} />
                                                <span>{booking.travelers} guest(s)</span>
                                            </div>
                                        </div>

                                        <div className="action-buttons-group">
                                            <button 
                                                className="action-btn-modern" 
                                                title="View Details"
                                                onClick={() => handleViewDetails(booking)}
                                            >
                                                <Eye size={15} /> View
                                            </button>
                                            
                                            {canCancel(booking.fullData?.status || '') && (
                                                <button
                                                    className="action-btn-modern"
                                                    title="Cancel booking"
                                                    onClick={() => openCancelModal(booking)}
                                                >
                                                    <Ban size={15} /> Cancel
                                                </button>
                                            )}

                                            {canDelete(booking.fullData?.status || '') && (
                                                <button
                                                    className="action-btn-modern delete"
                                                    title="Delete"
                                                    onClick={() => openDeleteModal(booking)}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state-modern">
                        <Compass size={48} />
                        <h3>No bookings found</h3>
                        <p>Try adjusting your search criteria</p>
                    </div>
                )}
            </div>

            {/* Delete Modal */}
            {isDeleteModalOpen && bookingToDelete && (
                <div className="modal-overlay" onClick={() => !isDeleting && setIsDeleteModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Delete Booking?</h3>
                        <p className="delete-modal-desc">
                            Are you sure you want to permanently delete this activity booking? This action cannot be undone.
                        </p>

                        <div className="delete-summary">
                            <span>{bookingToDelete.hotel} ({bookingToDelete.bookingReference})</span>
                        </div>

                        {deleteError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {deleteError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel-modal"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Deleting...
                                    </>
                                ) : (
                                    "Delete Booking"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Modal */}
            {isCancelModalOpen && bookingToCancel && (
                <div className="modal-overlay" onClick={() => !isCancelling && setIsCancelModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Cancel this booking?</h3>
                        <p className="delete-modal-desc">
                            This will cancel your activity booking. You can always book again later.
                        </p>

                        <div className="delete-summary">
                            <span>{bookingToCancel.hotel} ({bookingToCancel.bookingReference})</span>
                        </div>

                        {cancelError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {cancelError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel-modal"
                                onClick={() => setIsCancelModalOpen(false)}
                                disabled={isCancelling}
                            >
                                Keep Booking
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmCancel}
                                disabled={isCancelling}
                            >
                                {isCancelling ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Cancelling...
                                    </>
                                ) : (
                                    "Cancel Booking"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {isDetailsModalOpen && selectedBooking && (
                <div className="modal-overlay" onClick={() => setIsDetailsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Compass size={20} /> Booking Details</h3>
                            <button className="modal-close-btn" onClick={() => setIsDetailsModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        {detailsLoading ? (
                            <div className="loading-state-modern">
                                <Loader2 className="spinner-modern" size={32} />
                                <span>Loading details...</span>
                            </div>
                        ) : (
                            <div className="modal-body">
                                <div className="modal-section">
                                    <div className="modal-section-title"><Compass size={14} /> Activity Info</div>
                                    <div className="details-grid">
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Activity</span>
                                            <span className="detail-value-modal">{selectedBooking.hotel}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Location</span>
                                            <span className="detail-value-modal">{selectedBooking.destination}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Date</span>
                                            <span className="detail-value-modal">{selectedBooking.startDate}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Guests</span>
                                            <span className="detail-value-modal">{selectedBooking.travelers}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Amount</span>
                                            <span className="detail-value-modal">OMR {selectedBooking.amount.toLocaleString()}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Status</span>
                                            <span className={`badge-status-modal badge-${selectedBooking.status.toLowerCase()}`}>
                                                {selectedBooking.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-section">
                                    <div className="modal-section-title"><Users size={14} /> Contact Information</div>
                                    <div className="details-grid">
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Name</span>
                                            <span className="detail-value-modal">{selectedBooking.fullData?.firstname} {selectedBooking.fullData?.lastname}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">Email</span>
                                            <span className="detail-value-modal">{selectedBooking.fullData?.email}</span>
                                        </div>
                                        <div className="detail-item-modal">
                                            <span className="detail-label-modal">WhatsApp</span>
                                            <span className="detail-value-modal">{selectedBooking.fullData?.whatsapp_number}</span>
                                        </div>
                                    </div>
                                </div>

                                {selectedBooking.fullData?.notes && (
                                    <div className="modal-section">
                                        <div className="modal-section-title"><FileText size={14} /> Notes</div>
                                        <p style={{ fontSize: '14px', color: '#1a1a2e', margin: 0 }}>
                                            {selectedBooking.fullData.notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </CustomerLayout>
    );
};

export default MyTrips;